import { create } from "zustand";

/**
 * StructureComposer Store (générique)
 * - Objectif: maintenir une structure arborescente (tree) en mémoire côté UI.
 * - Source de vérité métier: Backend (.NET/PostgreSQL). Le store sert à l'édition locale.
 *
 * Modèle attendu:
 * {
 *   entities: {
 *     [id]: {
 *       id: string,
 *       type: string,
 *       parentId: string | null,
 *       childrenIds: string[],
 *       code?: string,
 *       label: string,
 *       metadata?: object
 *     }
 *   },
 *   rootIds: string[]
 * }
 */

const useStructureComposerStore = create((set, get) => ({
  // -----------------------------
  // State
  // -----------------------------
  entities: {},         // dictionnaire d'entités par id
  rootIds: [],          // liste ordonnée des racines (parentId = null)
  selectedId: null,     // id sélectionné (pour Inspector / actions)

  // -----------------------------
  // Initialisation
  // -----------------------------
  setInitialData: (data) =>
    set({ entities: data.entities || {}, rootIds: data.rootIds || [], selectedId: null }),

  reset: () => set({ entities: {}, rootIds: [], selectedId: null }),

  // -----------------------------
  // Sélection
  // -----------------------------
  selectNode: (id) => set({ selectedId: id }),

  // -----------------------------
  // CRUD - Nodes
  // -----------------------------

  /**
   * Ajoute une entité.
   * - Si parentId est null => devient une racine (rootIds)
   * - Sinon => ajoutée à childrenIds du parent
   */
  addNode: ({ id, type, parentId = null, label = "", code = "", metadata = {} }) =>
    set((state) => {
      const newEntity = { id, type, parentId, childrenIds: [], label, code, metadata };
      const entities = { ...state.entities, [id]: newEntity };

      if (parentId && entities[parentId]) {
        entities[parentId] = { ...entities[parentId], childrenIds: [...entities[parentId].childrenIds, id] };
      }

      return { entities, rootIds: parentId ? state.rootIds : [...state.rootIds, id] };
    }),

  /**
   * Met à jour une entité (merge shallow).
   * Ex: updateNode(id, { label: "New label" })
   */
  updateNode: (id, updates) =>
    set((state) => ({ entities: { ...state.entities, [id]: { ...state.entities[id], ...updates } } })),

  /**
   * Supprime une entité et tous ses descendants.
   * - Retire aussi l'id du parent (childrenIds) ou des roots (rootIds)
   * - Réinitialise selectedId si nécessaire
   */
  removeNode: (id) =>
    set((state) => {
      const entities = { ...state.entities };
      const target = entities[id];
      if (!target) return state;

      const removeRecursively = (nodeId) => {
        entities[nodeId]?.childrenIds.forEach(removeRecursively);
        delete entities[nodeId];
      };
      removeRecursively(id);

      if (target.parentId && entities[target.parentId]) {
        entities[target.parentId] = {
          ...entities[target.parentId],
          childrenIds: entities[target.parentId].childrenIds.filter((c) => c !== id),
        };
      }

      return {
        entities,
        rootIds: state.rootIds.filter((r) => r !== id),
        selectedId: state.selectedId === id ? null : state.selectedId,
      };
    }),

  /**
   * Déplace une entité sous un nouveau parent (reparenting).
   * - newParentId = null => devient racine
   * Note: ici on append en fin de childrenIds; l'indexing fin sera géré au niveau UI/drag&drop.
   */
  moveNode: (id, newParentId) =>
    set((state) => {
      const entities = { ...state.entities };
      const node = entities[id];
      if (!node) return state;

      if (node.parentId && entities[node.parentId]) {
        entities[node.parentId] = {
          ...entities[node.parentId],
          childrenIds: entities[node.parentId].childrenIds.filter((c) => c !== id),
        };
      }

      if (newParentId && entities[newParentId]) {
        entities[newParentId] = { ...entities[newParentId], childrenIds: [...entities[newParentId].childrenIds, id] };
      }

      return {
        entities: { ...entities, [id]: { ...node, parentId: newParentId } },
        rootIds: newParentId ? state.rootIds.filter((r) => r !== id) : [...state.rootIds, id],
      };
    }),

  // -----------------------------
  // Export
  // -----------------------------

  /**
   * Retourne un payload sérialisable pour l'API.
   * (Le mapping final backend peut être fait ailleurs si besoin.)
   */
  serialize: () => {
    const { entities, rootIds } = get();
    return { entities, rootIds };
  },
}));

export default useStructureComposerStore;
